/**
 * Cart store — POS savatcha holati (Zustand + immer)
 *
 * State:
 *   - items: CartItem[] (mahsulot, qty, narx, chegirma)
 *   - paymentType, cashAmount, cardAmount, transferAmount
 *   - customerId, note
 *
 * Actions:
 *   - addItem, removeItem, updateQuantity, updateDiscount
 *   - setPaymentType, setCashAmount, setCardAmount, setTransferAmount
 *   - setCustomer, setNote
 *   - clearCart
 *
 * Computed (selector hooks):
 *   - totalAmount() — items subtotal yig'indisi
 *   - changeAmount() — totalPaid - total
 *   - debtAmount() — total - totalPaid
 *
 * Persistence:
 *   - sessionStorage (faqat brauzer tab davomida)
 *   - Refresh'da yo'qolmaydi, lekin tab yopilsa tozalanadi
 */

import type { PaymentType, ProductDto, ProductVariant } from '@avtofix/shared';
import { buildVariantLabel, convertToUZS } from '@avtofix/shared';
import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

/**
 * Cart Item — parent yoki variant.
 *
 * Composite ID:
 *   - Parent: `cartItemId = product.id`
 *   - Variant: `cartItemId = `${product.id}-v${variantIndex}``
 *
 * Bir variant 2 marta scan qilinsa cartItemId mos kelib, quantity ortadi.
 * Parent va variant alohida cart qator.
 */
export interface CartItem {
  /** Composite ID — bu yagona identifikator (productId yoki productId-vIndex). */
  cartItemId: string;
  /** Parent mahsulot (variant bo'lsa ham, parent referans saqlanadi). */
  product: Pick<
    ProductDto,
    'id' | 'name' | 'sku' | 'price' | 'currency' | 'stock' | 'image' | 'unit'
  >;
  /** Variant sotuvi bo'lsa: index + variant snapshot. Parent sotuvi: null. */
  variantIndex: number | null;
  variantSnapshot: {
    name: string;
    sku: string | null;
    price: number;
    stock: number;
    currency: 'UZS' | 'USD' | 'RUB' | 'CNY';
  } | null;
  quantity: number;
  unitPrice: number; // override mumkin (mahsulot/variant price'idan farqli)
  discount: number; // ±so'm har birlik uchun
}

/**
 * POS-Variant: addItem variantInfo argumenti.
 */
export interface AddItemVariantInfo {
  variantIndex: number;
  variant: ProductVariant;
}

interface CartState {
  items: CartItem[];
  paymentType: PaymentType;
  cashAmount: number;
  cardAmount: number;
  transferAmount: number;
  customerId: string | null;
  note: string;
  lastAddedId: string | null;

  // Computed
  totalAmount: () => number;
  changeAmount: () => number;
  debtAmount: () => number;
  totalPaid: () => number;
  itemCount: () => number;

  // Actions — cartItemId composite identifikator bilan ishlaydi
  addItem: (product: CartItem['product'], qty?: number, variantInfo?: AddItemVariantInfo) => void;
  removeItem: (cartItemId: string) => void;
  updateQuantity: (cartItemId: string, qty: number) => void;
  updateDiscount: (cartItemId: string, discount: number) => void;
  setUnitPrice: (cartItemId: string, price: number) => void;
  selectItem: (cartItemId: string | null) => void;
  setPaymentType: (type: PaymentType) => void;
  setCashAmount: (amount: number) => void;
  setCardAmount: (amount: number) => void;
  setTransferAmount: (amount: number) => void;
  setCustomer: (id: string | null) => void;
  setNote: (note: string) => void;
  clearCart: () => void;
}

/** Composite cartItemId — parent yoki variant. */
export function makeCartItemId(productId: string, variantIndex: number | null): string {
  return variantIndex === null ? productId : `${productId}-v${variantIndex}`;
}

/** Display helpers — variant bo'lsa variant ma'lumotidan, aks holda product'dan. */
export function getItemDisplayName(item: CartItem): string {
  return item.variantSnapshot?.name ?? item.product.name;
}

export function getItemDisplaySku(item: CartItem): string | null {
  return item.variantSnapshot?.sku ?? item.product.sku;
}

export function getItemStock(item: CartItem): number {
  return item.variantSnapshot?.stock ?? item.product.stock;
}

const calcSubtotal = (item: CartItem): number => {
  return Math.max(0, item.unitPrice - item.discount) * item.quantity;
};

export const useCartStore = create<CartState>()(
  devtools(
    persist(
      immer((set, get) => ({
        items: [],
        paymentType: 'CASH',
        cashAmount: 0,
        cardAmount: 0,
        transferAmount: 0,
        customerId: null,
        note: '',
        lastAddedId: null,

        totalAmount: () => {
          return get().items.reduce((sum, item) => sum + calcSubtotal(item), 0);
        },

        totalPaid: () => {
          const { cashAmount, cardAmount, transferAmount } = get();
          return cashAmount + cardAmount + transferAmount;
        },

        changeAmount: () => {
          const total = get().totalAmount();
          const paid = get().totalPaid();
          return Math.max(0, paid - total);
        },

        debtAmount: () => {
          const total = get().totalAmount();
          const paid = get().totalPaid();
          return Math.max(0, total - paid);
        },

        itemCount: () => {
          return get().items.reduce((sum, item) => sum + item.quantity, 0);
        },

        addItem: (product, qty = 1, variantInfo) => {
          set((state) => {
            const variantIndex = variantInfo?.variantIndex ?? null;
            const cartItemId = makeCartItemId(product.id, variantIndex);
            const existing = state.items.find((i) => i.cartItemId === cartItemId);
            if (existing !== undefined) {
              existing.quantity += qty;
            } else {
              const v = variantInfo?.variant;
              // Kassa BITTA valyutada (so'm) ishlaydi: USD/RUB/CNY mahsulot narxi
              // savatga qo'shilganda kursda so'mga aylantiriladi (convertToUZS,
              // defaultRatesToUZS: 1$=12500). Shunda jami/to'lov/buyurtma/dashboard
              // hammasi so'mda — aralash valyuta yig'indisi buzilmaydi.
              const rawPrice = v?.price ?? product.price;
              const rawCurrency = v?.currency ?? product.currency;
              const uzsPrice = Math.round(convertToUZS(rawPrice, rawCurrency));
              state.items.push({
                cartItemId,
                product,
                variantIndex,
                variantSnapshot:
                  v !== undefined
                    ? {
                        // To'liq nom (name+value) — autoGroup'da name=prefiks bug'i
                        name: buildVariantLabel(v),
                        sku: v.sku ?? null,
                        price: uzsPrice,
                        stock: v.stock ?? 0,
                        currency: 'UZS',
                      }
                    : null,
                quantity: qty,
                unitPrice: uzsPrice,
                discount: 0,
              });
            }
            state.lastAddedId = cartItemId;
          });
        },

        removeItem: (cartItemId) => {
          set((state) => {
            state.items = state.items.filter((i) => i.cartItemId !== cartItemId);
            if (state.lastAddedId === cartItemId) {
              state.lastAddedId = state.items.at(-1)?.cartItemId ?? null;
            }
          });
        },

        updateQuantity: (cartItemId, qty) => {
          set((state) => {
            const item = state.items.find((i) => i.cartItemId === cartItemId);
            if (item === undefined) return;
            if (qty <= 0) {
              state.items = state.items.filter((i) => i.cartItemId !== cartItemId);
              if (state.lastAddedId === cartItemId) {
                state.lastAddedId = state.items.at(-1)?.cartItemId ?? null;
              }
              return;
            }
            item.quantity = qty;
          });
        },

        updateDiscount: (cartItemId, discount) => {
          set((state) => {
            const item = state.items.find((i) => i.cartItemId === cartItemId);
            if (item === undefined) return;
            item.discount = Math.max(0, discount);
          });
        },

        setUnitPrice: (cartItemId, price) => {
          set((state) => {
            const item = state.items.find((i) => i.cartItemId === cartItemId);
            if (item === undefined) return;
            item.unitPrice = Math.max(0, price);
          });
        },

        selectItem: (cartItemId) => {
          set((state) => {
            // Faqat mavjud bo'lgan cart item'ni tanlash mumkin (yoki null — bekor)
            if (cartItemId !== null && !state.items.some((i) => i.cartItemId === cartItemId)) {
              return;
            }
            state.lastAddedId = cartItemId;
          });
        },

        setPaymentType: (type) => {
          set((state) => {
            state.paymentType = type;
          });
        },
        setCashAmount: (v) => {
          set((state) => {
            state.cashAmount = Math.max(0, v);
          });
        },
        setCardAmount: (v) => {
          set((state) => {
            state.cardAmount = Math.max(0, v);
          });
        },
        setTransferAmount: (v) => {
          set((state) => {
            state.transferAmount = Math.max(0, v);
          });
        },
        setCustomer: (id) => {
          set((state) => {
            state.customerId = id;
          });
        },
        setNote: (note) => {
          set((state) => {
            state.note = note;
          });
        },

        clearCart: () => {
          set((state) => {
            state.items = [];
            state.cashAmount = 0;
            state.cardAmount = 0;
            state.transferAmount = 0;
            state.customerId = null;
            state.note = '';
            state.paymentType = 'CASH';
            state.lastAddedId = null;
          });
        },
      })),
      {
        name: 'avtofix-cart',
        storage: createJSONStorage(() => sessionStorage),
        partialize: (state) => ({
          items: state.items,
          paymentType: state.paymentType,
          customerId: state.customerId,
          note: state.note,
          lastAddedId: state.lastAddedId,
        }),
      },
    ),
    { name: 'CartStore' },
  ),
);
